import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useFetcher } from '@/compose/axios'

const defaultAgencyData = {
  pickup: '',
  dropoff: '',
  car: null,
  performance: '',
  number_of_passengers: null,
  main_passenger: '',
  other_language: '',
  pickup_specific: '',
  number_suitcases: '',
  notes: '',
  amount: null,
  file_number: '',
  type_of_service: ''
}

export const useUserStore = defineStore('user', () => {
  const user = ref(null)
  const agencyData = ref({ ...defaultAgencyData })
  const token = computed(() => user.value?.token)
  const isLoggedIn = computed(() => !!user.value?.id)
  const fullName = computed(() => {
    return isLoggedIn.value ? user.value.name + ' ' + user.value.last_name : false
  })

  function fill(data) {
    user.value = data
    localStorage.setItem('user', JSON.stringify(data))
  }

  function update(data) {
    user.value = { ...user.value, ...data }
    localStorage.setItem('user', JSON.stringify(user.value))
  }

  function updateAgency(data) {
    agencyData.value = { ...agencyData.value, ...data }
    sessionStorage.setItem('agencyData', JSON.stringify(agencyData.value))
  }

  function preventLogout() {
    user.value = null
    localStorage.removeItem('user')
  }

  function clearAgencyData() {
    agencyData.value = {
      pickup: '',
      dropoff: '',
      car: null,
      performance: '',
      number_of_passengers: null,
      main_passenger: '',
      other_language: '',
      pickup_specific: '',
      number_suitcases: '',
      notes: '',
      amount: null,
      file_number: '',
      type_of_service: ''
    }
    sessionStorage.removeItem('agencyData')
  }

  const getUserInfo = async () => {
    const { axiosInstance } = useFetcher({ baseUrl: import.meta.env.VITE_APP_API_URL })
    try {
      const res = await axiosInstance.get('/user/info')
      if (res?.data?.data) {
        update(res.data.data)
      }
      return res
    } catch (error) {
      // Error handling if needed
      return Promise.reject(error)
    }
  }

  const editUserSubmit = async (data) => {
    const { axiosInstance } = useFetcher({ baseUrl: import.meta.env.VITE_APP_API_URL })
    try {
      await axiosInstance.patch('/users/' + data.user_id, data)
      update(data)
      return true
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const fillData = () => {
    const userStorage = localStorage.getItem('user')
    if (userStorage) {
      try {
        fill(JSON.parse(userStorage))
      } catch (e) {
        fill(null)
      }
    } else {
      fill(null)
    }
  }

  const fillAgency = () => {
    const sessionStorageData = sessionStorage.getItem('agencyData')
    if (sessionStorageData) {
      try {
        updateAgency(JSON.parse(sessionStorageData))
      } catch (e) {
        // Ignore JSON parsing errors
      }
    }
  }

  fillData()
  fillAgency()

  return {
    agencyData,
    token,
    user,
    fullName,
    isLoggedIn,
    fill,
    update,
    updateAgency,
    preventLogout,
    getUserInfo,
    editUserSubmit,
    clearAgencyData
  }
})
